import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box } from '@usealloy/component-library';
import { UserContext } from '~/dashboard/context';

interface AiSummaryCardProps {
  token: string;
  type?: string;
  inline?: boolean;
  style?: React.CSSProperties;
}

export const AiSummaryCard = ({
  token,
  type,
  inline = false,
  style = {},
}: AiSummaryCardProps) => {
  const {
    featureFlags: { llmEvaluationSummaryEmbedded },
    agent: { email, isEmployee },
  } = useContext(UserContext);
  const canView = llmEvaluationSummaryEmbedded && (
    isEmployee || email.endsWith('+ai@alloy.com')
  );

  const queryParams = new URLSearchParams();

  if (type) {
    queryParams.set('type', type);
  }

  if (inline) {
    queryParams.set('inline', 'true');
  }

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(inline ? 80 : 300);

  const onMessage = useCallback(({ data: message, origin, source }) => {
    if (source !== iframeRef.current?.contentWindow) {
      return;
    }

    if (origin !== window.location.origin) {
      return;
    }

    if (message?.source !== 'embedded-dashboard') {
      return;
    }

    switch (message.type) {
      case 'resize':
        setIframeHeight(message.data.height);
        break;
      default:
        break;
    }
  }, [setIframeHeight]);

  useEffect(() => {
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  if (!canView) {
    return null;
  }

  return (
    <Box style={style}>
      <iframe
        ref={iframeRef}
        width="100%"
        height={iframeHeight}
        style={{ border: 'none' }}
        src={`${window.origin}/embedded/summary/${token}?${queryParams.toString()}`}
      />
    </Box>
  );
};
